// ORCA API client.
//
//   UI  →  hooks (hooks/queries.ts, TanStack Query)  →  api (this file)  →  ORCA API
//
// Today every method resolves local mock data, validated by the same Zod
// schemas the real API responses will be validated with. To connect the
// backend, replace `fromMock(...)` with `request(...)` per method — pages
// and components do not change.
import type { z } from 'zod';
import * as S from './schemas';

import { getMock } from '@/dev/mockState';
import i18n from '@/i18n';
import { MOCK_DATA } from './mode';
import { alertChannels, apiMessage, realApi, type NewsQuery, type SendNowOutcome, type SocialQuery } from './real';
import type { SocialXPage, SocialYtPage } from '@/types';

/** Error the UI can show: generic failure or no connection. */
export class ApiError extends Error {
  constructor(message: string, readonly kind: 'error' | 'offline' = 'error') { super(message); }
}
const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

type Opts<T> = {
  /** page data: follows the dev loading / error / offline switches */
  page?: boolean;
  /** what this endpoint returns when there is nothing yet (dev «empty») */
  empty?: (d: T) => T;
};

// Mocks are imported lazily so they stay out of the main bundle.
async function fromMock<T extends z.ZodTypeAny>(schema: T, load: () => Promise<unknown>, opts: Opts<z.infer<T>> = {}): Promise<z.infer<T>> {
  const m = getMock();
  if (opts.page) {
    if (m.data === 'loading') await new Promise(() => {}); // stays in the skeleton until the switch changes
    if (m.data === 'offline' || !navigator.onLine) { await delay(400); throw new ApiError(i18n.t('nav:error.offline'), 'offline'); }
    if (m.data === 'error') { await delay(500); throw new ApiError(i18n.t('nav:error.page')); }
  }
  let d = schema.parse(await load()) as z.infer<T>;
  if (m.data === 'empty' && opts.empty) d = opts.empty(d);
  return d;
}

// Future: `const BASE = import.meta.env.VITE_ORCA_API_URL` and
// `request(schema, '/v1/…')` doing fetch + `schema.parse(await res.json())`.

export const api = {
  // Phase 6A pages (account, market quotes, portfolio, news) read the ORCA API;
  // the DEV mock server answers only in a development build opened with ?dev=
  account: () => MOCK_DATA ? fromMock(S.Account, async () => (await import('@/mock/account')).accountFor((await import('@/mock/profile')).currentPlan()), { empty: (d) => ({ ...d, coins: [] }) }) : realApi.account(),
  market: () => MOCK_DATA ? fromMock(S.Market, async () => (await import('@/mock/market')).market, { page: true }) : realMarket(),
  dashboard: () => fromMock(S.Dashboard, async () => (await import('@/mock/market')).dashboard, { page: true }),
  portfolio: () => !MOCK_DATA ? realApi.portfolio() : fromMock(S.Portfolio, async () => {
    const p = await (await import('@/mock/portfolio')).portfolioServer.get();
    if (getMock().portfolio === 'empty') return { ...p, holdings: [] };
    // positions exist only for tracked coins (bot): an account that was always on Free
    // has positions only in its one coin; after a downgrade the old ones stay (as in the bot)
    const prof = await import('@/mock/profile');
    if (!prof.neverPaidFree()) return p;
    const coins = (await import('@/mock/account')).accountFor(prof.currentPlan()).coins;
    return { ...p, holdings: p.holdings.filter((h) => coins.includes(h.symbol)) };
  }, { page: true, empty: (d) => ({ ...d, holdings: [] }) }),
  // feeds are sent for the plan: content of a locked feature never leaves the server (mock/redact)
  signals: () => !MOCK_DATA ? realApi.signals() : fromMock(S.Signals, async () => (await import('@/mock/redact')).signalsForPlan((await import('@/mock/signals')).signals), { page: true }),
  calendar: () => !MOCK_DATA ? realApi.calendar() : fromMock(S.Calendar, async () => (await import('@/mock/calendar')).calendar, { page: true, empty: (d) => ({ ...d, events: [] }) }),
  /** DEV mock only: the demo feed the mock pages are built from (the ORCA API serves pages, never the whole feed) */
  news: () => fromMock(S.News, async () => (await import('@/mock/redact')).newsForPlan((await import('@/mock/news')).news), { page: true, empty: (d) => ({ ...d, items: [], ideas: [] }) }),
  ideas: async () => (!MOCK_DATA ? realApi.ideas() : (await api.news()).ideas),
  /** the News page: one page and its counters — the server in real mode, the same rules over the mock feed in DEV */
  newsPage: async (q: NewsQuery, tz: string) => {
    if (!MOCK_DATA) return realApi.newsPage(q);
    const n = await api.news();
    return (await import('@/features/news/page')).pageFromItems(n.items, q, tz, n.lastVisit, n.updatedAt);
  },
  wallStreet: () => !MOCK_DATA ? realApi.wallStreet() : fromMock(S.WallStreet, async () => (await import('@/mock/wallstreet')).wallStreet, { page: true }),
  social: () => !MOCK_DATA ? realApi.social() : fromMock(S.Social, async () => (await import('@/mock/redact')).socialForPlan((await import('@/mock/wallstreet')).social), { page: true, empty: (d) => ({ ...d, posts: [], videos: [], coins: [] }) }),
  /** one page of X Radar / YouTube (7 days); DEV mock: the same paging over the demo feed */
  socialX: async (q: SocialQuery): Promise<SocialXPage> => {
    if (!MOCK_DATA) return realApi.socialX(q);
    const all = await api.social();
    const posts = q.coin ? all.posts.filter((p) => (p.coins ?? [p.symbol]).includes(q.coin!)) : all.posts;
    const counts: Record<string, number> = { '*': all.posts.length };
    for (const p of all.posts) for (const c of new Set(p.coins ?? [p.symbol])) if (c) counts[c] = (counts[c] ?? 0) + 1;
    const pages = Math.max(1, Math.ceil(posts.length / q.perPage)), page = Math.min(q.page, pages);
    return { available: true, posts: posts.slice((page - 1) * q.perPage, page * q.perPage), page, pages, total: posts.length, perPage: q.perPage, counts, coins: all.coins };
  },
  socialYt: async (q: SocialQuery): Promise<SocialYtPage> => {
    if (!MOCK_DATA) return realApi.socialYt(q);
    const all = await api.social();
    const videos = q.coin ? all.videos.filter((v) => v.symbols.includes(q.coin!)) : all.videos;
    const pages = Math.max(1, Math.ceil(videos.length / q.perPage)), page = Math.min(q.page, pages);
    return { available: true, videos: videos.slice((page - 1) * q.perPage, page * q.perPage), page, pages, total: videos.length, perPage: q.perPage };
  },
  // ORCA API: Orca AI answers come from the server (aiApi); this is only the page's
  // copy — suggested questions and the signal ring's labels, never answers or history
  orcaAI: () => !MOCK_DATA ? realOrcaAI() : fromMock(S.OrcaAI, async () => { const m = await import('@/mock/ai'); return (await import('@/mock/redact')).aiForPlan(m.orcaAI(), m.withoutPaidSignals()); }, { empty: (d) => ({ ...d, chats: [] }) }),
  profile: () => !MOCK_DATA ? realApi.profile() : fromMock(S.Profile, async () => (await import('@/mock/profile')).profileServer.get(), { page: true }),
  plans: () => !MOCK_DATA ? realApi.plans() : fromMock(S.PlanOffers, async () => (await import('@/mock/profile')).offersView()),
  // what a locked feature already found: the ORCA API does not count it yet — nothing rather than a made-up number
  teasers: () => !MOCK_DATA ? Promise.resolve(S.Teasers.parse({})) : fromMock(S.Teasers, async () => (await import('@/mock/teasers')).teasersFor()),
  planCatalog: () => fromMock(S.PlanCatalog, async () => (await import('@/mock/planCatalog')).planCatalog()),
  alerts: () => !MOCK_DATA ? realApi.alerts() : fromMock(S.Alerts, async () => (await import('@/mock/redact')).alertsForPlan(await (await import('@/mock/alerts')).alertsServer.get()), {
    page: true,
    empty: (d) => ({ ...d, whale: { ...d.whale, feed: [] }, price: { ...d.price, history: [], coins: [] } }),
  }),
  notices: () => !MOCK_DATA ? realApi.notices() : fromMock(S.Notices, async () => (await import('@/mock/alerts')).noticesServer.list(), { empty: () => [] }),
};

// Profile actions. Each returns the updated profile (validated like a query).
// ORCA API: the account is the session's; trial, cancel and payments follow
// the bot's rules on the server. The DEV mock server mirrors them.
const srv = async () => (await import('@/mock/profile')).profileServer;
const P = (run: () => Promise<unknown>) => fromMock(S.Profile, run);
type CheckoutStart = { plan: string; months: number; price: number; method: 'card' | 'crypto'; renew: boolean };
export const profileApi = {
  /** the site's display time zone — web-only, never sent to the account or Telegram */
  setTimezone: (tz: string) => !MOCK_DATA ? realApi.setTimezone(tz) : P(async () => (await srv()).setTimezone(tz)),
  endSession: (id: string) => !MOCK_DATA ? realApi.endSession(id) : P(async () => (await srv()).endSession(id)),
  endOtherSessions: () => !MOCK_DATA ? realApi.endOtherSessions() : P(async () => (await srv()).endOtherSessions()),
  /** /continue after Free PAUSED — in Telegram for now (the ORCA API has no resume yet) */
  resume: () => !MOCK_DATA ? Promise.reject(new Error(i18n.t('server:apiErr.telegram_only'))) : P(async () => (await srv()).resume()),
  ackDowngrade: () => !MOCK_DATA ? realApi.profile() : P(async () => (await srv()).ackDowngrade()),
  startTrial: () => !MOCK_DATA ? realApi.startTrial() : P(async () => (await srv()).startTrial()),
  /** «Прислать новости сейчас» → Telegram (the bot's delivery); DEV mock: a sent result after a short wait */
  sendNewsNow: (): Promise<SendNowOutcome> => !MOCK_DATA ? realApi.sendNewsNow() : new Promise((ok) => window.setTimeout(() => ok('sent'), 1200)),
  cancelPlan: () => !MOCK_DATA ? realApi.cancelPlan() : P(async () => (await srv()).cancelPlan()),
  /** a pending payment; `checkoutUrl` is the provider's page (ORCA API) — the mock has none */
  createPayment: async (o: CheckoutStart): Promise<{ id: string | null; checkoutUrl: string | null }> => {
    if (!MOCK_DATA) return realApi.createPayment(o);
    await (await srv()).createPayment(o);
    return { id: null, checkoutUrl: null };
  },
  /** the payment's state from the server; throws `{ code: 'pending' | 'failed' | … }` until it is paid and applied */
  checkPayment: (id?: string | null) => (!MOCK_DATA && id ? realApi.checkPayment(id) : P(async () => (await srv()).checkPayment())),
};

/** Orca AI: one account quota shared with Telegram (ORCA API); the mock keeps its demo answers. */
export const aiApi = {
  quota: () => realApi.aiQuota(),
  ask: (message: string) => realApi.aiAsk(message),
  result: (id: string) => realApi.aiResult(id),
};

// Account settings: the single store of coins, deliveries / intervals and the
// AI quota. Each returns the updated account. Future: PATCH /v1/account/...
const acc = async () => {
  const [{ accountServer }, { currentPlan }] = await Promise.all([import('@/mock/account'), import('@/mock/profile')]);
  return { s: accountServer, plan: currentPlan() };
};
const AC = (run: () => Promise<unknown>) => fromMock(S.Account, run);
export const accountApi = {
  setCoins: (coins: string[]) => !MOCK_DATA ? realApi.setCoins(coins) : AC(async () => { const a = await acc(); return a.s.setCoins(a.plan, coins); }),
  setDelivery: (key: string, p: { enabled?: boolean; value?: number }) => !MOCK_DATA ? realApi.setDelivery(key, p) : AC(async () => { const a = await acc(); return a.s.setDelivery(a.plan, key, p); }),
  setLanguage: (language: 'ru' | 'en' | 'de') => !MOCK_DATA ? realApi.setLanguage(language) : AC(async () => { const a = await acc(); return a.s.setLanguage(a.plan, language); }),
  // the DEV mock's local AI counter (the ORCA API counts the shared quota itself: aiApi)
  spendAi: () => !MOCK_DATA ? realApi.account() : AC(async () => { const a = await acc(); return a.s.spendAi(a.plan); }),
};

// Portfolio: positions are purchases (lots). Each returns the updated
// portfolio with the aggregate computed by the server. Future: /v1/portfolio.
const pfs = async () => (await import('@/mock/portfolio')).portfolioServer;
const PF = (run: () => Promise<unknown>) => fromMock(S.Portfolio, run);
type Lot = import('@/types').PurchaseLot;
export const portfolioApi = {
  addLot: (symbol: string, lot: Lot) => !MOCK_DATA ? realApi.addLot(symbol, lot) : PF(async () => (await pfs()).addLot(symbol, lot)),
  updateLot: (symbol: string, id: string, lot: Omit<Lot, 'id'>) => !MOCK_DATA ? realApi.updateLot(symbol, id, lot) : PF(async () => (await pfs()).updateLot(symbol, id, lot)),
  removeLot: (symbol: string, id: string) => !MOCK_DATA ? realApi.removeLot(symbol, id) : PF(async () => (await pfs()).removeLot(symbol, id)),
  removePosition: (symbol: string) => !MOCK_DATA ? realApi.removePosition(symbol) : PF(async () => (await pfs()).removePosition(symbol)),
};

// Price history: the date check for an approximate purchase period (the bot's
// one-shot Binance klines lookup). Future: /v1/history/check.
export const historyApi = {
  checkPeriod: (symbol: string, price: number, period: string) => !MOCK_DATA ? realApi.checkPeriod(symbol, price, period) : fromMock(S.HistoryCheck, async () => (await import('@/mock/history')).checkPeriod(symbol, price, period)),
  /** NEW: estimate a purchase price — a day's close or a month's median close (+ range) */
  estimatePrice: (symbol: string, when: { day: string } | { period: string }) => !MOCK_DATA ? realApi.estimatePrice(symbol, when) : fromMock(S.PriceEstimate, async () => (await import('@/mock/history')).estimatePrice(symbol, when)),
};

// Alert settings (same switches as the bot). Each returns the updated alerts.
const al = async () => (await import('@/mock/alerts')).alertsServer;
const A = (run: () => Promise<unknown>) => fromMock(S.Alerts, run);
export const alertsApi = {
  setWhale: (p: { enabled?: boolean; networks?: string[]; threshold?: number }) => !MOCK_DATA ? explainAlerts(realApi.setOrcaWatch(p)) : A(async () => (await al()).setWhale(p)),
  setPrice: (p: { enabled?: boolean }) => !MOCK_DATA ? explainAlerts(realApi.setPriceAlerts(p)) : A(async () => (await al()).setPrice(p)),
  /** web-only: where urgent alerts show up in this browser */
  setChannels: (p: { site?: boolean }) => {
    if (MOCK_DATA) return A(async () => (await al()).setChannels(p));
    alertChannels.set({ ...alertChannels.get(), ...p });
    return realApi.alerts();
  },
};
const explainAlerts = <T,>(p: Promise<T>) => p.catch((e) => { throw new Error(apiMessage(e)); });

// Notifications. Live alerts: future WebSocket/SSE `/v1/stream`; the mock
// plays a short script so urgent pop-ups can be seen.
export const noticesApi = {
  markRead: async (ids: string[] | 'all') => {
    if (!MOCK_DATA) { await realApi.markNoticesRead(ids); return realApi.notices(); }
    return fromMock(S.Notices, async () => (await import('@/mock/alerts')).noticesServer.markRead(ids));
  },
  /** demo live events (DEV mock); the ORCA API bell is polled (NoticesProvider) */
  subscribe: async (cb: (e: import('@/mock/alerts').LiveEvent) => void) => (!MOCK_DATA ? () => {} : (await import('@/mock/alerts')).subscribeLive(cb)),
  test: async () => (await import('@/mock/alerts')).testEvent(),
};

/** The AI page's copy for the ORCA API: suggested questions and ring labels in the
 *  account's language, without canned answers, demo history or demo numbers. */
// only questions that state no market fact (the demo's «why is ETH falling» would be invented here)
const NEUTRAL_Q = ['today', 'fomc', 'cpi', 'fear', 'yields', 'vix', 'ethSol', 'whale', 'priceAlerts'];
const ORBIT_Q: Record<string, string> = { price: 'ethSol', cal: 'fomc', ws: 'vix', pie: 'today', news: 'today', x: 'today', whale: 'whale', link: 'priceAlerts' };
async function realOrcaAI() {
  const m = await import('@/mock/ai');
  const d = m.orcaAI();
  const qOf = Object.fromEntries(Object.entries(d.answers).map(([q, a]) => [a.id ?? '', q]));
  const neutral = new Set(NEUTRAL_Q.map((id) => qOf[id]).filter(Boolean));
  // `requires` stays: suggestions never offer a question about a feature the plan doesn't open
  const answers = Object.fromEntries(Object.entries(d.answers).filter(([q]) => neutral.has(q))
    .map(([q, a]) => [q, { known: '', scenario: '', risk: '', ...(a.requires ? { requires: a.requires } : {}) }]));
  const keep = (qs: string[]) => { const out = qs.filter((q) => neutral.has(q)); return out.length ? out : [qOf.today]; };
  return S.OrcaAI.parse({
    ...d, answers, fallback: { known: '', scenario: '', risk: '' }, chats: [],
    orbit: d.orbit.map((n) => ({ ...n, value: '', question: qOf[ORBIT_Q[n.icon] ?? 'today'] ?? n.question })),
    // the first placeholder is «Ask Orca»; the rest are demo questions
    placeholders: [d.placeholders[0], ...keep(d.placeholders.slice(1))],
    chatSuggestions: keep(d.chatSuggestions),
    askContexts: Object.fromEntries(Object.entries(d.askContexts).map(([r, c]) => [r, { ...c, suggestions: keep(c.suggestions) }])),
  });
}

/** Real quotes for the coin universe. The sentiment gauges are not served by the
 *  ORCA API yet (Overview, outside Phase 6A): they still come from the demo data. */
async function realMarket() {
  const [universe, demo] = await Promise.all([realApi.universe(), import('@/mock/market')]);
  return S.Market.parse({ universe, sentiment: demo.market.sentiment });
}
